import { useEffect, useMemo, useState } from "react";
import { Loader2, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { useCrm } from "./CrmContext";
import { api, errorMessage } from "@/lib/api";
import { CALL_OUTCOMES } from "@/lib/crm";

const TABS = [["note", "Note"], ["call", "Log call"], ["text", "Text"], ["email", "Email"]];
const fill = (text, lead, user) => {
  const agent = user?.name || user?.email || "";
  const values = { first_name: lead.first_name, last_name: lead.last_name, agent_name: agent, agent_first: agent.split(" ")[0] };
  return (text || "").replace(/\{(\w+)\}/g, (m, k) => values[k] ?? m);
};

export const Composer = ({ lead, mode, setMode, onLogged }) => {
  const { templates, sms, user, isOwner, go } = useCrm();
  const [text, setText] = useState("");
  const [outcome, setOutcome] = useState("Talked");
  const [templateId, setTemplateId] = useState("");
  const [busy, setBusy] = useState(false);
  const channel = mode === "email" ? "email" : "text";
  const options = useMemo(() => templates.filter((t) => t.channel === channel), [templates, channel]);
  const template = templates.find((t) => t.id === templateId);
  useEffect(() => {
    setText(""); setTemplateId("");
    if (mode === "email") { const first = templates.find((t) => t.channel === "email"); if (first) { setTemplateId(first.id); setText(fill(first.body, lead, user)); } }
  }, [mode, lead.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const choose = (tid) => { setTemplateId(tid); const t = templates.find((x) => x.id === tid); if (t) setText(fill(t.body, lead, user)); };

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setBusy(true);
    try {
      let data;
      if (mode === "email") ({ data } = await api.post(`/admin/leads/${lead.id}/email`, { template_id: templateId, message: text }));
      else if (mode === "text") ({ data } = await api.post(`/admin/leads/${lead.id}/text`, { message: text }));
      else ({ data } = await api.post(`/admin/leads/${lead.id}/activities`, { kind: mode, detail: text, outcome: mode === "call" ? outcome : "" }));
      onLogged(data); setText(""); if (mode === "email" && template) setText(fill(template.body, lead, user));
      toast.success(mode === "email" ? "Email sent" : mode === "text" ? "Text sent" : mode === "call" ? "Call logged" : "Note saved");
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const blocked = (mode === "email" && !lead.email) || (mode === "text" && (!lead.phone || !sms.configured));
  return (
    <form onSubmit={submit} className="crm-card overflow-hidden" data-testid="person-composer">
      <div className="flex border-b border-[#E2E8F0] bg-[#F8FAFC]">
        {TABS.map(([id, label]) => <button key={id} type="button" onClick={() => setMode(id)} className={`px-4 py-2.5 text-[13px] font-medium transition-colors ${mode === id ? "border-b-2 border-[#2B8FD6] bg-white text-[#1F78BD]" : "text-[#64748B] hover:text-[#0F172A]"}`} data-testid={`composer-tab-${id}`}>{label}</button>)}
      </div>
      <div className="space-y-3 p-4">
        {mode === "call" && (
          <div className="flex flex-wrap items-center gap-2">
            {lead.phone ? <a href={`tel:${lead.phone}`} className="crm-btn bg-[#16A34A] text-white hover:bg-[#15803D]" data-testid="composer-dial"><Phone className="h-4 w-4" />Call {lead.phone}</a> : <span className="text-[12px] text-[#8592A0]" data-testid="composer-no-phone">No phone on file — you can still log a call.</span>}
            {CALL_OUTCOMES.map((o) => <button key={o} type="button" onClick={() => setOutcome(o)} className={`rounded-full px-3 py-1 text-[12px] ${outcome === o ? "bg-[#3A4553] text-white" : "bg-[#F1F5F9]"}`} data-testid={`call-outcome-${o.toLowerCase().replace(/\s/g, "-")}`}>{o}</button>)}
          </div>
        )}
        {["text", "email"].includes(mode) && (
          <div className="flex flex-wrap items-center gap-2">
            <select value={templateId} onChange={(e) => choose(e.target.value)} className="crm-input h-8 w-auto text-[12px]" data-testid="composer-template">
              {mode === "text" && <option value="">Templates…</option>}{options.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <span className="text-[12px] text-[#64748B]">To <span className="font-mono">{mode === "email" ? lead.email || "no email on file" : lead.phone || "no phone on file"}</span></span>
          </div>
        )}
        {mode === "email" && template && <p className="rounded-md bg-[#F8FAFC] px-3 py-2 text-[12.5px]" data-testid="composer-subject"><span className="crm-label mr-2">Subject</span>{fill(template.subject, lead, user)}</p>}
        {mode === "text" && !sms.configured && (
          <p className="rounded-md border border-[#FDE68A] bg-[#FFFBEB] px-3 py-2 text-[12.5px] text-[#92400E]" data-testid="composer-sms-off">
            Texting isn't connected yet. {isOwner ? <button type="button" onClick={() => go("texting")} className="font-semibold underline" data-testid="composer-connect-sms">Connect texting in 4 steps</button> : "Ask Rob to connect it under Texting."}
          </p>
        )}
        <textarea value={text} onChange={(e) => setText(e.target.value)} rows={mode === "email" ? 6 : 3} maxLength={mode === "text" ? 640 : 4000} className="crm-input" data-testid="composer-input"
          placeholder={mode === "note" ? "Add a private note… (only your team sees notes)" : mode === "call" ? "What did you talk about?" : mode === "text" ? "Write a text message" : "Write your email"} />
        <div className="flex items-center justify-between">
          <span className="font-mono text-[11px] text-[#94A3B8]">{mode === "text" ? `${text.length}/640 · ${Math.max(1, Math.ceil(text.length / 160))} segment(s)` : mode === "email" ? `From ${user?.name || "you"} · replies go to ${user?.email}` : ""}</span>
          <button disabled={busy || blocked || !text.trim()} className="crm-btn crm-btn-primary disabled:opacity-40" data-testid="composer-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}{mode === "email" ? "Send email" : mode === "text" ? "Send text" : mode === "call" ? "Log call" : "Save note"}</button>
        </div>
      </div>
    </form>
  );
};
